import OpenInNewIcon from '@material-design-icons/svg/filled/open_in_new.svg?react';
import { Link } from 'react-router';
import {
  CopyableValue,
  Disclosure,
  Field,
  FieldGrid,
  Section,
} from '../../../components/common/detailParts';
import { TypedStatusBadge } from '../../../components/common/StatusBadge';
import { MetadataJsonViewer } from '../../../components/MetadataJsonViewer';
import {
  formatStageName,
  getHubspotTicketUrl,
} from '../../../lib/hubspot-management/formatters';
import { cn } from '../../../lib/utils';
import {
  Callout,
  Chip,
  Empty,
  EmptyState,
  ExpandableRow,
  Flag,
  List,
} from '../../../components/common/detailListParts';
import {
  findReleaseTicket,
  type Release,
  releaseSteps,
  type ReleaseStep,
  releaseTicketPath,
  releaseVersion,
  type Ticket,
  ticketQueuePath,
  ticketTime,
} from '../dappSignals';
import type { SuperDappTabProps } from '../types';

const ACTION_LINK_CLASS =
  'text-xs font-medium text-teal-300 transition-colors hover:text-teal-200';

function shortDate(timestamp: number, now = new Date()) {
  const date = new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  });
}

export function ReleasesTab({
  data,
  formatDate,
  formatBytes,
}: SuperDappTabProps) {
  const { releases, hubspotTickets, dapp } = data;

  return (
    <Section
      title="Releases"
      trailing={
        <div className="flex items-center gap-4 text-xs">
          {dapp.packageName && (
            <Link
              to={ticketQueuePath({ search: dapp.packageName })}
              className={ACTION_LINK_CLASS}
            >
              Tickets for this app →
            </Link>
          )}
          <span className="tabular-nums text-neutral-500">
            {releases.length}
          </span>
        </div>
      }
    >
      {releases.length === 0 ? (
        <EmptyState message="No releases yet" />
      ) : (
        <List>
          {releases.map((release, index) => (
            <ReleaseRow
              key={release.id}
              release={release}
              ticket={findReleaseTicket(release, hubspotTickets)}
              ticketPath={releaseTicketPath(release, hubspotTickets)}
              defaultOpen={index === 0}
              formatDate={formatDate}
              formatBytes={formatBytes}
            />
          ))}
        </List>
      )}
    </Section>
  );
}

interface ReleaseRowProps {
  release: Release;
  ticket: Ticket | undefined;
  ticketPath: string | null;
  defaultOpen?: boolean;
  formatDate: SuperDappTabProps['formatDate'];
  formatBytes: SuperDappTabProps['formatBytes'];
}

function ReleaseRow({
  release,
  ticket,
  ticketPath,
  defaultOpen,
  formatDate,
  formatBytes,
}: ReleaseRowProps) {
  return (
    <ExpandableRow
      defaultOpen={defaultOpen}
      summary={
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <span className="text-sm font-medium tabular-nums text-white">
            {releaseVersion(release)}
          </span>
          <TypedStatusBadge type="release" status={release.status} />
          <TypedStatusBadge
            type="submission"
            status={release.submissionStatus}
          />
          {release.hubspotTicketId && (
            <Chip mono>#{release.hubspotTicketId}</Chip>
          )}
          {release.alertCount > 0 && (
            <span className="text-xs text-neutral-500">
              {release.alertCount}{' '}
              {release.alertCount === 1 ? 'alert' : 'alerts'}
            </span>
          )}
          <span className="ml-auto flex items-center gap-3 text-xs text-neutral-500">
            <span className="tabular-nums">
              {formatBytes(release.releaseFileSize)}
            </span>
            <span className="tabular-nums">{formatDate(release.created)}</span>
          </span>
        </div>
      }
    >
      <ReleaseDetails
        release={release}
        ticket={ticket}
        ticketPath={ticketPath}
        formatDate={formatDate}
        formatBytes={formatBytes}
      />
    </ExpandableRow>
  );
}

const STEP_DOT: Record<ReleaseStep['state'], string> = {
  done: 'bg-teal-400',
  current: 'border border-amber-300',
  pending: 'bg-neutral-700',
  failed: 'bg-rose-400',
};

const STEP_TEXT: Record<ReleaseStep['state'], string> = {
  done: 'text-neutral-200',
  current: 'text-amber-200',
  pending: 'text-neutral-500',
  failed: 'text-rose-300',
};

/** The review pipeline for one release, with the date each stage was reached. */
function ReleaseTimeline({
  steps,
  formatDate,
}: {
  steps: ReleaseStep[];
  formatDate: SuperDappTabProps['formatDate'];
}) {
  return (
    <ol
      aria-label="Release timeline"
      className="flex flex-wrap gap-x-5 gap-y-2"
    >
      {steps.map((step) => (
        <li key={step.label} className="flex items-center gap-2 text-xs">
          <span
            aria-hidden="true"
            className={cn('size-2 shrink-0 rounded-full', STEP_DOT[step.state])}
          />
          <span className={cn('font-medium', STEP_TEXT[step.state])}>
            {step.label}
            <span className="sr-only">
              {step.state === 'done' ? '' : ` (${step.state})`}
            </span>
          </span>
          {step.at !== null && (
            <span
              className="tabular-nums text-neutral-600"
              title={formatDate(step.at)}
            >
              {shortDate(step.at)}
            </span>
          )}
        </li>
      ))}
    </ol>
  );
}

function TicketCard({
  release,
  ticket,
  ticketPath,
  formatDate,
}: {
  release: Release;
  ticket: Ticket | undefined;
  ticketPath: string | null;
  formatDate: SuperDappTabProps['formatDate'];
}) {
  const hubspotId = ticket?.hubspotId ?? release.hubspotTicketId;
  if (!hubspotId) {
    return null;
  }
  const closedAt = ticketTime(ticket?.closedDate);

  return (
    <div className="rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2.5">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm">
          <span className="text-neutral-500">Review ticket</span>
          <span className="font-mono text-white/90">#{hubspotId}</span>
          {ticket?.mappedSubmissionStatus && (
            <TypedStatusBadge
              type="submission"
              status={ticket.mappedSubmissionStatus}
            />
          )}
          {ticket?.hsPipelineStage && (
            <span className="text-xs text-neutral-500">
              {formatStageName(ticket.hsPipelineStage)}
            </span>
          )}
          {closedAt !== null && (
            <span className="text-xs text-neutral-500">
              · closed {formatDate(closedAt)}
              {ticket?.closedLabel ? ` · ${ticket.closedLabel}` : ''}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs">
          {ticketPath && (
            <Link to={ticketPath} className={ACTION_LINK_CLASS}>
              Open in Tickets →
            </Link>
          )}
          <a
            href={getHubspotTicketUrl(hubspotId)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-neutral-400 transition-colors hover:text-white"
          >
            HubSpot
            <OpenInNewIcon className="size-3 fill-current" />
          </a>
        </div>
      </div>
      {ticket?.reviewerNote && (
        <p className="mt-2 whitespace-pre-wrap text-xs leading-relaxed text-neutral-400">
          {ticket.reviewerNote}
        </p>
      )}
    </div>
  );
}

function ReleaseDetails({
  release,
  ticket,
  ticketPath,
  formatDate,
  formatBytes,
}: Omit<ReleaseRowProps, 'defaultOpen'>) {
  const hasNftData =
    release.nftMintAddress ||
    release.shortDescription ||
    release.longDescription ||
    release.newInVersion;
  const hasTechnical =
    release.releaseFileHash ||
    release.certificateFingerprint ||
    release.permissions.length > 0 ||
    release.locales.length > 0;

  return (
    <div className="flex flex-col gap-5 border-l border-white/5 pl-4">
      <ReleaseTimeline steps={releaseSteps(release)} formatDate={formatDate} />

      {(release.processingError || release.rejectionReason) && (
        <div className="flex flex-col gap-2">
          {release.processingError && (
            <Callout label="Processing error:">
              {release.processingError}
            </Callout>
          )}
          {release.rejectionReason && (
            <Callout label="Rejection:">{release.rejectionReason}</Callout>
          )}
        </div>
      )}

      <TicketCard
        release={release}
        ticket={ticket}
        ticketPath={ticketPath}
        formatDate={formatDate}
      />

      <FieldGrid cols={4}>
        <Field label="Version code">
          <span className="tabular-nums text-neutral-300">
            {release.versionCode ?? '—'}
          </span>
        </Field>
        <Field label="File size">
          <span className="tabular-nums text-neutral-300">
            {formatBytes(release.releaseFileSize)}
          </span>
        </Field>
        <Field label="SDK">
          <span className="tabular-nums text-neutral-300">
            {release.minSdkVersion ?? '—'}
            <span className="mx-1.5 text-neutral-600">→</span>
            {release.targetSdkVersion ?? '—'}
          </span>
        </Field>
        <Field label="Upload provider">
          <span className="text-neutral-300">
            {release.uploadProvider ?? '—'}
          </span>
        </Field>
        <Field label="Package" className="col-span-2">
          {release.androidPackage ? (
            <CopyableValue value={release.androidPackage} />
          ) : (
            <Empty />
          )}
        </Field>
        <Field label="Collection verified">
          <Flag on={release.isCollectionVerified}>
            {release.isCollectionVerified ? 'Verified' : 'Unverified'}
          </Flag>
        </Field>
        <Field label="Approval email">
          <span className="text-neutral-300">
            {formatDate(release.approvalEmailSentAt)}
          </span>
        </Field>
      </FieldGrid>

      {hasTechnical && (
        <Disclosure label="Technical">
          <div className="flex flex-col gap-5">
            {(release.releaseFileHash || release.certificateFingerprint) && (
              <FieldGrid cols={2}>
                {release.releaseFileHash && (
                  <Field label="APK SHA-256">
                    <CopyableValue
                      value={release.releaseFileHash}
                      truncate={false}
                      className="text-xs"
                    />
                  </Field>
                )}
                {release.certificateFingerprint && (
                  <Field label="Certificate SHA-256">
                    <CopyableValue
                      value={release.certificateFingerprint}
                      truncate={false}
                      className="text-xs"
                    />
                  </Field>
                )}
              </FieldGrid>
            )}
            {release.locales.length > 0 && (
              <Field label={`Locales · ${release.locales.length}`}>
                <div className="flex flex-wrap gap-1.5">
                  {release.locales.map((locale, index) => (
                    <Chip key={`${locale}-${index}`} mono>
                      {locale}
                    </Chip>
                  ))}
                </div>
              </Field>
            )}
            {release.permissions.length > 0 && (
              <Field label={`Permissions · ${release.permissions.length}`}>
                <div className="flex flex-wrap gap-1.5">
                  {release.permissions.map((permission) => (
                    <Chip key={permission} mono>
                      {permission.replace('android.permission.', '')}
                    </Chip>
                  ))}
                </div>
              </Field>
            )}
          </div>
        </Disclosure>
      )}

      {hasNftData && (
        <Disclosure label="NFT & listing copy">
          <NftDetails release={release} />
        </Disclosure>
      )}

      {release.hubspotSubmissions.length > 0 && (
        <Disclosure
          label="HubSpot submissions"
          count={release.hubspotSubmissions.length}
        >
          <List>
            {release.hubspotSubmissions.map((submission) => (
              <div
                key={submission.id}
                className="flex items-center justify-between gap-3 py-2 first:pt-0 last:pb-0"
              >
                <span className="min-w-0 truncate text-xs text-neutral-400">
                  {submission.submissionType} ·{' '}
                  {formatDate(submission.submittedAt)}
                </span>
                <span
                  className={`shrink-0 text-xs ${submission.succeeded ? 'text-teal-400' : 'text-rose-400'}`}
                >
                  {submission.succeeded ? 'Succeeded' : 'Failed'}
                </span>
              </div>
            ))}
          </List>
        </Disclosure>
      )}
    </div>
  );
}

function NftDetails({ release }: { release: Release }) {
  const cluster = release.nftCluster || 'mainnet';

  return (
    <div className="flex flex-col gap-5">
      {release.nftMintAddress && (
        <FieldGrid cols={2}>
          <Field label="NFT mint">
            <CopyableValue
              value={release.nftMintAddress}
              href={`https://explorer.solana.com/address/${release.nftMintAddress}?cluster=${cluster}`}
            />
          </Field>
          {release.nftTransactionSignature && (
            <Field label="NFT transaction">
              <CopyableValue
                value={release.nftTransactionSignature}
                href={`https://explorer.solana.com/tx/${release.nftTransactionSignature}?cluster=${cluster}`}
              />
            </Field>
          )}
        </FieldGrid>
      )}

      {release.newInVersion && (
        <Field label="What's new">
          <p className="whitespace-pre-wrap leading-relaxed text-neutral-300">
            {release.newInVersion}
          </p>
        </Field>
      )}
      {release.shortDescription && (
        <Field label="Short description">
          <span className="text-neutral-300">{release.shortDescription}</span>
        </Field>
      )}
      {release.longDescription && (
        <Field label="Long description">
          <p className="whitespace-pre-wrap leading-relaxed text-neutral-300">
            {release.longDescription}
          </p>
        </Field>
      )}

      {release.nftMetadataUri && (
        <Field label="Metadata URI">
          <div className="flex flex-col gap-3">
            <CopyableValue
              value={release.nftMetadataUri}
              href={release.nftMetadataUri}
            />
            <MetadataJsonViewer uri={release.nftMetadataUri} />
          </div>
        </Field>
      )}
    </div>
  );
}
